import { useEffect, useState, type ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  api,
  CATEGORY_LABEL,
  COMPANY_LABEL,
  CONTRACT_KIND_LABEL,
  SSL_KIND_LABEL,
  STATUS_LABEL,
  alertBadgeVariant,
  daysUntil,
  formatDate,
  formatDateTime,
  formatYen,
  tagList,
  type Contract,
  type Customer,
  type Domain,
  type Report,
  type Server,
  type SslCertificate,
} from '../components/api'
import { Alert, Badge, Card, EmptyState } from '../components/ui'

type Tab = 'basic' | 'servers' | 'domains' | 'contracts' | 'reports'

const TAB_LABEL: Record<Tab, string> = {
  basic: '基本',
  servers: 'サーバー',
  domains: 'ドメイン／SSL',
  contracts: '保守契約',
  reports: '対応履歴',
}

type DomainWithSsl = Domain & { ssl: SslCertificate[] }

export function CustomerDetailPage() {
  const { id } = useParams()
  const customerId = id ? Number(id) : null

  const [customer, setCustomer] = useState<Customer | null>(null)
  const [servers, setServers] = useState<Server[]>([])
  const [domains, setDomains] = useState<DomainWithSsl[]>([])
  const [contracts, setContracts] = useState<Contract[]>([])
  const [reports, setReports] = useState<Report[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState<Tab>('basic')

  useEffect(() => {
    if (!customerId) return
    let cancelled = false
    setLoading(true)

    const loadDomains = async (): Promise<DomainWithSsl[]> => {
      const { items } = await api<{ items: Domain[] }>('/api/domains', {
        query: { customer_id: customerId },
      })
      const withSsl = await Promise.all(
        items.map(async (d) => {
          try {
            const ssl = await api<{ items: SslCertificate[] }>(
              `/api/domains/${d.id}/ssl`,
            )
            return { ...d, ssl: ssl.items ?? [] }
          } catch {
            return { ...d, ssl: [] }
          }
        }),
      )
      return withSsl
    }

    Promise.all([
      api<Customer>(`/api/customers/${customerId}`),
      api<{ items: Server[] }>(`/api/customers/${customerId}/servers`),
      loadDomains(),
      api<{ items: Contract[] }>('/api/contracts', {
        query: { customer_id: customerId },
      }),
      api<{ items: Report[] }>(`/api/customers/${customerId}/reports`),
    ])
      .then(([c, s, d, k, r]) => {
        if (cancelled) return
        setCustomer(c)
        setServers(s.items ?? [])
        setDomains(d)
        setContracts(k.items ?? [])
        setReports(r.items ?? [])
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [customerId])

  if (loading) return <p className="muted">読み込み中…</p>
  if (error) return <Alert variant="danger">{error}</Alert>
  if (!customer) return <Alert variant="warning">顧客が見つかりません</Alert>

  return (
    <>
      <div className="page-header">
        <div>
          <h1 style={{ marginBottom: 4 }}>{customer.company_name}</h1>
          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <Badge
              variant={
                customer.status === 'active'
                  ? 'success'
                  : customer.status === 'paused'
                    ? 'warning'
                    : 'muted'
              }
            >
              {STATUS_LABEL[customer.status]}
            </Badge>
            {customer.industry && (
              <Badge variant="muted">{customer.industry}</Badge>
            )}
            {tagList(customer.tags).map((t) => (
              <Badge key={t} variant="muted">
                {t}
              </Badge>
            ))}
          </div>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <Link
            to={`/customers/${customer.id}/edit`}
            className="btn btn-secondary btn-sm"
          >
            編集
          </Link>
          <Link to="/customers" className="btn btn-ghost btn-sm">
            一覧へ戻る
          </Link>
        </div>
      </div>

      <Card>
        <div className="row" style={{ gap: 4, flexWrap: 'wrap' }}>
          {(Object.keys(TAB_LABEL) as Tab[]).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={tab === t ? 'btn btn-primary btn-sm' : 'btn btn-ghost btn-sm'}
            >
              {TAB_LABEL[t]}
              {t === 'servers' && ` (${servers.length})`}
              {t === 'domains' && ` (${domains.length})`}
              {t === 'contracts' && ` (${contracts.length})`}
              {t === 'reports' && ` (${reports.length})`}
            </button>
          ))}
        </div>
      </Card>

      {tab === 'basic' && <BasicTab customer={customer} />}
      {tab === 'servers' && <ServersTab servers={servers} />}
      {tab === 'domains' && <DomainsTab domains={domains} />}
      {tab === 'contracts' && <ContractsTab contracts={contracts} />}
      {tab === 'reports' && (
        <ReportsTab reports={reports} customerId={customer.id} />
      )}
    </>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <small className="muted">{label}</small>
      <div>{children}</div>
    </div>
  )
}

function BasicTab({ customer }: { customer: Customer }) {
  return (
    <Card title="基本情報">
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 16,
        }}
      >
        <Field label="社名">{customer.company_name}</Field>
        <Field label="請求名">{customer.billing_name ?? '同上'}</Field>
        <Field label="業種">{customer.industry ?? '—'}</Field>
        <Field label="状態">{STATUS_LABEL[customer.status]}</Field>
        <Field label="営業担当">
          {customer.sales_user_name ?? '—'}
          {customer.sales_user_company && (
            <span className="subtle">
              {' '}
              （{COMPANY_LABEL[customer.sales_user_company]}）
            </span>
          )}
        </Field>
        <Field label="制作担当">
          {customer.dev_user_name ?? '—'}
          {customer.dev_user_company && (
            <span className="subtle">
              {' '}
              （{COMPANY_LABEL[customer.dev_user_company]}）
            </span>
          )}
        </Field>
        <Field label="登録日">{formatDate(customer.created_at)}</Field>
        <Field label="最終更新">{formatDate(customer.updated_at)}</Field>
      </div>
      {customer.notes && (
        <>
          <hr
            style={{
              border: 0,
              borderTop: '1px solid var(--c-border)',
              margin: '16px 0',
            }}
          />
          <Field label="メモ">
            <pre
              style={{
                margin: 0,
                whiteSpace: 'pre-wrap',
                fontFamily: 'inherit',
              }}
            >
              {customer.notes}
            </pre>
          </Field>
        </>
      )}
    </Card>
  )
}

function ServersTab({ servers }: { servers: Server[] }) {
  if (servers.length === 0) {
    return (
      <Card>
        <EmptyState title="サーバーは未登録です" />
      </Card>
    )
  }
  return (
    <Card title="サーバー">
      <table className="table">
        <thead>
          <tr>
            <th>種別</th>
            <th>プラン</th>
            <th>管理URL</th>
            <th>契約期限</th>
            <th style={{ width: 80 }}></th>
          </tr>
        </thead>
        <tbody>
          {servers.map((s) => {
            const days = daysUntil(s.contract_expires_at)
            return (
              <tr key={s.id}>
                <td>{s.kind}</td>
                <td>{s.plan ?? '—'}</td>
                <td className="mono">
                  {s.admin_url ? (
                    <a href={s.admin_url} target="_blank" rel="noreferrer">
                      開く
                    </a>
                  ) : (
                    '—'
                  )}
                </td>
                <td>
                  {formatDate(s.contract_expires_at)}{' '}
                  {days !== null && (
                    <Badge variant={alertBadgeVariant(days)}>
                      {days >= 0 ? `あと${days}日` : `${-days}日経過`}
                    </Badge>
                  )}
                </td>
                <td>
                  <Link
                    to={`/servers/${s.id}/edit`}
                    className="btn btn-ghost btn-sm"
                  >
                    編集
                  </Link>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </Card>
  )
}

function DomainsTab({ domains }: { domains: DomainWithSsl[] }) {
  if (domains.length === 0) {
    return (
      <Card>
        <EmptyState title="ドメインは未登録です" />
      </Card>
    )
  }
  return (
    <Card title="ドメイン／SSL">
      <table className="table">
        <thead>
          <tr>
            <th>ドメイン</th>
            <th>レジストラ</th>
            <th>ドメイン期限</th>
            <th>自動更新</th>
            <th>SSL</th>
            <th>SSL 期限</th>
          </tr>
        </thead>
        <tbody>
          {domains.map((d) => {
            const dDays = daysUntil(d.expires_at)
            const ssl = d.ssl[0]
            const sDays = daysUntil(ssl?.expires_at)
            return (
              <tr key={d.id}>
                <td className="mono">{d.domain}</td>
                <td>{d.registrar ?? '—'}</td>
                <td>
                  {formatDate(d.expires_at)}{' '}
                  {dDays !== null && (
                    <Badge variant={alertBadgeVariant(dDays)}>
                      {dDays >= 0 ? `あと${dDays}日` : `${-dDays}日経過`}
                    </Badge>
                  )}
                </td>
                <td>{d.auto_renew ? '有効' : '—'}</td>
                <td>{ssl ? SSL_KIND_LABEL[ssl.kind] : '—'}</td>
                <td>
                  {ssl ? formatDate(ssl.expires_at) : '—'}{' '}
                  {sDays !== null && (
                    <Badge variant={alertBadgeVariant(sDays)}>
                      {sDays >= 0 ? `あと${sDays}日` : `${-sDays}日経過`}
                    </Badge>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </Card>
  )
}

function ContractsTab({ contracts }: { contracts: Contract[] }) {
  if (contracts.length === 0) {
    return (
      <Card>
        <EmptyState title="保守契約は未登録です" />
      </Card>
    )
  }
  return (
    <Card title="保守契約">
      <table className="table">
        <thead>
          <tr>
            <th>種別</th>
            <th className="text-right">月額</th>
            <th>期間</th>
            <th>自動更新</th>
            <th>状態</th>
          </tr>
        </thead>
        <tbody>
          {contracts.map((c) => {
            const active = c.ended_at === null
            return (
              <tr key={c.id}>
                <td>{CONTRACT_KIND_LABEL[c.kind]}</td>
                <td className="text-right">{formatYen(c.monthly_amount)}</td>
                <td>
                  {formatDate(c.started_at)} 〜 {formatDate(c.ended_at)}
                </td>
                <td>{c.auto_renew ? '有効' : '—'}</td>
                <td>
                  <Badge variant={active ? 'success' : 'muted'}>
                    {active ? '稼働中' : '終了'}
                  </Badge>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </Card>
  )
}

function ReportsTab({
  reports,
  customerId,
}: {
  reports: Report[]
  customerId: number
}) {
  if (reports.length === 0) {
    return (
      <Card
        actions={
          <Link
            to={`/reports/new?customer_id=${customerId}`}
            className="btn btn-primary btn-sm"
          >
            ＋ 対応履歴を登録
          </Link>
        }
        title="対応履歴"
      >
        <EmptyState title="対応履歴は未登録です" />
      </Card>
    )
  }
  return (
    <Card
      title="対応履歴"
      actions={
        <Link
          to={`/reports/new?customer_id=${customerId}`}
          className="btn btn-primary btn-sm"
        >
          ＋ 新規登録
        </Link>
      }
    >
      <table className="table">
        <thead>
          <tr>
            <th style={{ width: 140 }}>発生日時</th>
            <th style={{ width: 110 }}>カテゴリ</th>
            <th>内容</th>
            <th style={{ width: 90 }} className="text-right">
              工数(分)
            </th>
            <th style={{ width: 100 }}>起票者</th>
          </tr>
        </thead>
        <tbody>
          {reports.map((r) => (
            <tr key={r.id}>
              <td>{formatDateTime(r.occurred_at)}</td>
              <td>
                <Badge variant="muted">{CATEGORY_LABEL[r.category]}</Badge>
              </td>
              <td>{truncate(r.body, 80)}</td>
              <td className="text-right">{r.work_minutes}</td>
              <td className="muted">{r.user_name ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  )
}

function truncate(s: string, n: number) {
  if (!s) return ''
  return s.length > n ? `${s.slice(0, n)}…` : s
}
